import Phaser from 'phaser';
import type { TransitIntents } from '../../sim/transit';

/** Finger travel is scaled by this, so a short drag crosses the screen. */
const DRAG_GAIN = 1.3;
/** Keep the target this far inside the field (matches the sim's margin). */
const EDGE = 12;

/**
 * Transit steering (GDD: Controls). Drag anywhere: the ship moves by the finger's travel from
 * where it was when the finger landed, so the thumb never covers it. Arrows or WASD on a keyboard.
 */
export class TransitControls {
  private drag: {
    id: number;
    fromX: number;
    fromY: number;
    shipX: number;
    shipY: number;
    x: number;
    y: number;
  } | null = null;
  private readonly keys: Record<'up' | 'down' | 'left' | 'right', Phaser.Input.Keyboard.Key[]>;
  /** Off while the game is paused. */
  private enabled = true;

  constructor(
    scene: Phaser.Scene,
    private readonly ship: () => { x: number; y: number },
    private readonly field: () => { width: number; height: number },
  ) {
    scene.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      if (this.drag || !this.enabled) return;
      const s = this.ship();
      this.drag = { id: p.id, fromX: p.x, fromY: p.y, shipX: s.x, shipY: s.y, x: p.x, y: p.y };
    });
    scene.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (this.drag?.id !== p.id) return;
      this.drag.x = p.x;
      this.drag.y = p.y;
    });
    const lift = (p: Phaser.Input.Pointer) => {
      if (this.drag?.id === p.id) this.drag = null;
    };
    scene.input.on('pointerup', lift);
    scene.input.on('pointerupoutside', lift);

    const kb = scene.input.keyboard;
    const add = (...codes: number[]) => codes.map((c) => kb?.addKey(c)).filter((k) => !!k);
    const K = Phaser.Input.Keyboard.KeyCodes;
    this.keys = {
      up: add(K.UP, K.W),
      down: add(K.DOWN, K.S),
      left: add(K.LEFT, K.A),
      right: add(K.RIGHT, K.D),
    };
  }

  /** Turns steering on or off (the pause menu), letting go of any drag. */
  setEnabled(on: boolean): void {
    this.enabled = on;
    this.drag = null;
  }

  sample(): TransitIntents {
    const held = (keys: Phaser.Input.Keyboard.Key[]) => (keys.some((k) => k.isDown) ? 1 : 0);
    return {
      target: this.dragTarget(),
      moveX: held(this.keys.right) - held(this.keys.left),
      moveY: held(this.keys.down) - held(this.keys.up),
    };
  }

  /**
   * Where the drag puts the ship. Past an edge the drag is re-anchored, so turning the finger
   * around moves the ship straight back instead of first unwinding the travel past the edge.
   */
  private dragTarget(): { x: number; y: number } | null {
    const d = this.drag;
    if (!d) return null;
    const { width, height } = this.field();
    const x = Math.max(EDGE, Math.min(width - EDGE, d.shipX + (d.x - d.fromX) * DRAG_GAIN));
    const y = Math.max(EDGE, Math.min(height - EDGE, d.shipY + (d.y - d.fromY) * DRAG_GAIN));
    d.fromX = d.x - (x - d.shipX) / DRAG_GAIN;
    d.fromY = d.y - (y - d.shipY) / DRAG_GAIN;
    return { x, y };
  }
}
